import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Button, Form, Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";
import ReactPlayer from 'react-player'
import OnlineQuestions from "../../components/OnlineInduction/OnlineQuestions";
import Lottie from "react-lottie";
import animationData from "../../assets/lottie/RlzgDapIGa.json";
import errorLottie from "../../assets/lottie/404.json";
import { loadingStyles } from '../../components/JobApplication/Shared/Helpers';
import OnlineInductionSuccessPage from '../../components/OnlineInduction/OnlineInductionSuccessPage'
import SweetAlert from "react-bootstrap-sweetalert";
import { getToken } from '../../utils/tokenUtils';
function ManageInductionViewer() {

    const { id, inductionId, pin } = useParams(); // Induction ID & Pin (Guid)
    const [isLoading, setIsLoading] = useState(true);
    const [inductionData, setInductionData] = useState({});
    const [hasError, setHasError] = useState(false)
    const [errors, setErrors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmittingTextDisplay, setIsSubmittingTextDisplay] = useState('');
    const [saved, setSaved] = useState(false);
    const [alert, setAlert] = React.useState(null);
    const hideAlert = () => setAlert(null);

    // lottie data
    const animationOptions = {
        loop: true,
        autoplay: true,
        animationData: hasError ? errorLottie : animationData,
    };

    const handleChoiceChange = (questionId, choiceId, isChecked) => {
        const updatedInductionData = { ...inductionData };
        const question = updatedInductionData.questions.find((q) => q.questionId === questionId);
        errors[question.questionId] = null; // clear error message

        if (question) {
            const choice = question.choices.find((c) => c.id === choiceId);

            // only allow one answer to be checked
            question.choices.forEach((c) => {
                if (c.id !== choiceId) {
                    c.isChecked = false;
                }
            });

            if (choice) {
                choice.isChecked = isChecked;
                setInductionData(updatedInductionData);
            }
        }
    };

    const handleFreeTextAnswerChange = (questionId, value) => {
        setInductionData((prevInductionData) => {
            const updatedQuestions = prevInductionData.questions.map((question) => {
                if (question.questionId === questionId) {
                    // update question answer
                    errors[question.questionId] = null; // clear error message
                    return { ...question, answerText: value };
                }
                return question;
            });

            return { ...prevInductionData, questions: updatedQuestions };
        });
    };

    function validateForm() {
        const errors = {};
        let index = 1;

        // Loop through questions in inductionData and validate them
        inductionData.questions.forEach((question) => {

            if (question.questionType === 1) {
                // Check for unanswered multiple-choice questions
                const hasCheckedChoice = question.choices.some((choice) => choice.isChecked);
                if (!hasCheckedChoice) {
                    errors[question.questionId] = `Please select an answer for question ${index}.`;
                }
            }

            else {
                if (question.answerText === null || question.answerText.trim() === '') {
                    errors[question.questionId] = `Please provide an answer for question ${index}.`;
                }
            }

            index++;
        });

        return errors;
    }

    const showErrorDialog = (errorMessage) => {
        return new Promise((resolve) => {
            setAlert(
                <SweetAlert
                    error // Use 'error' instead of 'warning' for an error message
                    style={{ display: "block", marginTop: "-100px" }}
                    title="Oops..."
                    onConfirm={async () => {
                        hideAlert();
                        resolve(true);
                    }}
                    confirmBtnBsStyle="danger" // Use 'danger' style for the confirm button
                    showConfirm={true} // Show only the confirm button (no cancel button)
                >
                    {errorMessage} {/* Display the provided error message */}
                </SweetAlert>
            );
        });
    };


    const showConfirmationDialog = () => {
        return new Promise((resolve) => {
            setAlert(
                <SweetAlert
                    info
                    style={{ display: "block", marginTop: "-100px" }}
                    title="Submission Confirmation"
                    onConfirm={async () => {
                        hideAlert();
                        resolve(true);
                    }}
                    onCancel={() => {
                        hideAlert();
                        resolve(false);
                    }}
                    confirmBtnBsStyle="info"
                    cancelBtnBsStyle="danger"
                    cancelBtnCssClass="red-cancel-button" 
                    showCancel>
                    Please confirm your answers by pressing 'OK' before submitting your online induction.
                </SweetAlert>
            );
        });
    };


    const handleSubmit = async () => {
        try {
            const logErrors = validateForm();

            if (!Object.values(logErrors).some(Boolean)) {
                const readyToSubmit = await showConfirmationDialog();

                if (readyToSubmit) {
                    await commitSaveChanges(); // api call once confirmed
                }

            } else {
                setErrors(logErrors);
                window.scrollTo(0, 0);
            }
        } catch (e) {
            showErrorDialog("An error occurred while submitting the online induction.");
        } finally {
            //setIsSubmitting(false);
        }
    };



    const commitSaveChanges = async () => {
        try {
            setIsSubmitting(true);
            setIsSubmittingTextDisplay("Submitting Online Induction Answers, Please Do Not Refresh...")

            let url = '/fpmcareers/onlineInduction/SubmitOnlineInductionAnswers'; 

            if (process.env.NODE_ENV === 'development') {
                url = `/onlineInduction/SubmitOnlineInductionAnswers`;
            }

            const token = getToken();
            const response = await fetch(url, {
                method: 'post',
                headers: {
                    "Authorization": `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(inductionData),
            });

            if (response.ok) {
                setSaved(true);
            } else {
                throw new Error("An error occurred while submitting the online induction from the API.");
            }

        } catch (error) {
            showErrorDialog("An error occurred while submitting the online induction.");
        } finally {
            setIsSubmitting(false);
        }
    }



    const fetchOnlineInduction = async () => {
        try {

            let url = `/fpmcareers/onlineInduction/retrieveOnlineInductionLink?answerHeaderId=${id}&inductionId=${inductionId}&pin=${pin}`;

            // for debugging api
            if (process.env.NODE_ENV === 'development') {
                url = `/onlineInduction/retrieveOnlineInductionLink?answerHeaderId=${id}&inductionId=${inductionId}&pin=${pin}`;
            }

            const token = getToken();
            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                setHasError(true);
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const body = await response.json();

            // check if it returns view model from api
            if (!body || Object.keys(body).length === 0) {
                setHasError(true);
                return;
            }

            setInductionData(body);
        } catch (err) {
            setHasError(true);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setIsLoading(true);
        fetchOnlineInduction();
    }, []);


    if (hasError) {
        return (
            <Container fluid>
                <Row>
                    <Col md={{ span: 6, offset: 3 }} sm={12} className="text-center">
                        <Lottie
                            options={animationOptions}
                            height={450}
                            width={400}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col md={{ span: 8, offset: 2 }} sm={12} className="text-center">
                        <h2 className="saved-title">Opps...</h2>
                        <hr className="dashed-line" />

                    </Col>
                </Row>
                <Row>
                    <Col md={{ span: 8, offset: 2 }} sm={12}>
                        <p className="saved-text">
                            It seems like the page you're attempting to reach isn't available. Perhaps you mistyped the URL or the page has been removed or relocated. 
                        </p>
                    </Col>
                </Row>
            </Container>
        );
    }

    if (saved) {
        return (
            <OnlineInductionSuccessPage
                animationOptions={animationOptions}
                inductionData={inductionData}
            />
        );
    }

    return (
        <Container fluid>
            {alert}

            {isSubmitting && (
                <div style={loadingStyles.loadingScreen}>
                    <div style={loadingStyles.loadingSpinner}></div>
                    <div style={loadingStyles.loadingText}>{isSubmittingTextDisplay}</div>
                </div>
            )}

            {isLoading ? (
                <div
                    className="text-center"
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                    }}
                >
                    <Spinner
                        animation="border"
                        style={{
                            width: "200px",
                            height: "200px",
                            border: "9px solid #fff",
                            borderRadius: "50%",
                            borderTopColor: "#1abc9c",
                            fontSize: "60px",
                        }}
                    />
                    <h3>Loading Online Induction..</h3>
                </div>

            ) : (

                <Row className="mt-3">

                    <Col md={6} sm={12}>
                        <Card className="video-card">
                            <Card.Body>
                                <div className="video-container">
                                    <h2 className="video-title">{inductionData.title}</h2>
                                    <p>By FP McCann {new Date().toLocaleDateString('en-GB')}</p>
                                    <div className="video-player-wrapper">
                                        <div className="react-player-aspect-ratio">
                                            <ReactPlayer
                                                url={inductionData?.videoPathFile}
                                                controls
                                                width="100%"
                                                height="100%"
                                            />
                                        </div>
                                    </div>
                                </div>
                                    <h5 className="video-subtitle">{inductionData.description}.</h5>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={6} sm={12}>
                        <Card className="questions-card">
                            <Card.Body>
                                <OnlineQuestions
                                    questionsList={inductionData?.questions}
                                    onChoiceChange={handleChoiceChange}
                                    onFreeTextChange={handleFreeTextAnswerChange}
                                    errors={errors}
                                />
                            </Card.Body>
                        </Card>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Button
                                className="btn-lg-submit btn-shadow"
                                style={{
                                    backgroundColor: '#388E3C', // Darker green color
                                    color: 'white', // Text color
                                    borderColor: '#388E3C', // Border color
                                }}
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                Submit Answers
                            </Button>
                        </div>
                    </Col>

                </Row>
            )}
        </Container>
    );
}

export default ManageInductionViewer;
